<template>
    <v-row row wrap>

        <v-col cols="12" md="3" lg="2">
            <movimiento-filters
                v-model="filters"
                @updateFilters="setFilters"
                @clearFilter="clearFilters"
            />
        </v-col>

        <v-col cols="12" md="9" lg="10">

            <div class="table-fixed-header-wrap">
            <v-data-table class="mt-3" fixed-header height="calc(100vh - 300px)" :headers="headers" :items="items" :search="search" :single-expand="false"
                :server-items-length="totalItems" :loading="loading" :page.sync="pageNumber"
                :items-per-page.sync="itemsPerPage" :sort-by.sync="orderBy" :sort-desc.sync="orderDesc"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }" @update:page="fetch" @update:sort-by="fetch"
                @update:sort-desc="fetch" @update:items-per-page="fetch">

                <template v-slot:item.tipo="{ item }">
                    <v-chip :color="getTipoColor(item)" dark small>
                        <v-icon left small>{{ getTipoIcon(item) }}</v-icon>
                        {{ getTipoLabel(item) }}
                    </v-chip>
                </template>

                <template v-slot:item.precioTotal="{ item }">
                    <v-chip :color="getColor(item)" dark>
                        $ {{ item.precioTotal.toFixed(2) }}
                    </v-chip>
                </template>

                <template v-slot:item.descuento="{ item }">
                    <span v-if="item.descuento">{{ item.descuento }} %</span>
                </template>

                <template v-slot:item.recargo="{ item }">
                    <span v-if="item.recargo">{{ item.recargo }} %</span>
                </template>

                <template v-slot:item.fecha="{ item }">
                    {{ getDateTimeFormat(item.fecha) }}
                </template>

                <template v-slot:item.factura="{ item }">
                    <div class="d-flex align-center">
                        <template v-if="permiteEmitirFacturaPorTipo(item)">
                            <v-chip 
                                v-if="getFacturaEstado(item.id)" 
                                :color="getFacturaColor(item.id)" 
                                small 
                                dark
                                class="mr-2 factura-chip"
                            >
                                <v-icon left small>{{ getFacturaIcon(item.id) }}</v-icon>
                                {{ getFacturaTexto(item.id) }}
                            </v-chip>
                            <v-chip 
                                v-else 
                                color="grey" 
                                small 
                                outlined 
                                class="mr-2 factura-chip"
                            >
                                Sin factura
                            </v-chip>
                            <v-btn 
                                v-if="mostrarBotonEmitir(item)" 
                                x-small 
                                color="primary"
                                @click="emitirFacturaMovimiento(item)"
                            >
                                <v-icon left small>mdi-plus</v-icon>
                                Emitir
                            </v-btn>
                        </template>
                    </div>
                </template>

                <template slot="no-data">
                    <div class="text-xs-center" v-t="'common.noData'"></div>
                </template>

                <template slot="loading">
                    <div class="text-xs-center" v-t="'common.loading'"></div>
                </template>

                <template v-slot:item.action="{ item }">
                    <show-button @click="$emit('show', item)" />
                </template>

            </v-data-table>
            </div>
        </v-col>

    </v-row>
</template>

<script>
import MovimientoProvider from "../../../providers/MovimientoProvider";
import UserConfigurationProvider from "../../../../user-configuration/providers/UserConfigurationProvider";
import { MovimientoFilters } from "../MovimientoFilters";

import { ShowButton } from "@dracul/common-frontend"
import { DayjsMixin } from "@dracul/dayjs-frontend";
import FacturacionProvider from "../../../../facturacion/providers/FacturacionProvider";

export default {
    name: "MovimientoList",
    components: { ShowButton, MovimientoFilters },
    mixins: [DayjsMixin],
    data() {
        return {
            items: [],
            totalItems: null,
            loading: false,
            orderBy: 'fecha',
            orderDesc: true,
            itemsPerPage: 50,
            pageNumber: 1,
            search: '',
            filters: [
                { field: 'tipo',        operator: '$eq',   value: null },
                { field: 'fecha',       operator: '$gte',  value: null },
                { field: 'fecha',       operator: '$lte',  value: null },
                { field: 'medioDePago', operator: '$eq',   value: null },
                { field: 'precioTotal', operator: '$gte',  value: null },
                { field: 'precioTotal', operator: '$lte',  value: null },
                { field: 'descuento',   operator: '$gte',  value: null },
                { field: 'recargo',     operator: '$gte',  value: null },
                { field: 'facturado',   operator: '$eq',   value: null },
            ],
            facturasMap: {}
        }
    },
    computed: {
        headers() {
            const baseHeaders = [
                { text: this.$t('movimiento.labels.tipo'), value: 'tipo' },
                { text: 'Monto', value: 'precioTotal' },
                { text: this.$t('movimiento.labels.descuento'), value: 'descuento' },
                { text: this.$t('movimiento.labels.recargo'), value: 'recargo' },
                { text: this.$t('movimiento.labels.medioDePago'), value: 'medioDePago' },
                { text: this.$t('movimiento.labels.fecha'), value: 'fecha' },
            ];

            baseHeaders.push({ text: 'Factura', value: 'factura', sortable: false });

            // Siempre agregar columna de acciones al final
            baseHeaders.push({ text: this.$t('common.actions'), value: 'action', sortable: false });

            return baseHeaders;
        },
        getOrderBy() {
            return (Array.isArray(this.orderBy)) ? this.orderBy[0] : this.orderBy
        },
        getOrderDesc() {
            return (Array.isArray(this.orderDesc)) ? this.orderDesc[0] : this.orderDesc
        }
    },
    async created() {
        await this.updateDepositoFilter();
        this.fetch()
    },
    mounted() {
        // Escuchar eventos de actualizaci?n de configuraci?n
        this.$root.$on('userConfigurationUpdated', async () => {
            await this.updateDepositoFilter();
            this.fetch();
        });
    },
    beforeDestroy() {
        this.$root.$off('userConfigurationUpdated');
    },
    methods: {
        async updateDepositoFilter() {
            // Intentar obtener depositoId del cache de Apollo
            let depositoId = UserConfigurationProvider.getDepositoIdFromCache();

            // Si no est? en el cache, intentar cargar la configuraci?n del usuario
            if (!depositoId) {
                try {
                    const response = await UserConfigurationProvider.getUserConfiguration(false);
                    if (response && response.data && response.data.userConfigurationGet) {
                        depositoId = response.data.userConfigurationGet.depositoIdSeleccionado;
                    }
                } catch (error) {
                    // Si falla, intentar con network-only
                    try {
                        const response = await UserConfigurationProvider.getUserConfiguration(true);
                        if (response && response.data && response.data.userConfigurationGet) {
                            depositoId = response.data.userConfigurationGet.depositoIdSeleccionado;
                        }
                    } catch (err) {
                        console.error('Error loading user configuration:', err);
                    }
                }
            }

            // Buscar o crear el filtro de deposito
            let depositoFilterIndex = this.filters.findIndex(f => f.field === 'deposito' || f.field === 'depositoId');

            if (depositoId) {
                if (depositoFilterIndex >= 0) {
                    // Actualizar el filtro existente
                    this.filters[depositoFilterIndex].value = depositoId;
                    this.filters[depositoFilterIndex].operator = '$eq';
                    this.filters[depositoFilterIndex].field = 'deposito';
                } else {
                    // Agregar nuevo filtro
                    this.filters.push({
                        field: 'deposito',
                        operator: '$eq',
                        value: depositoId
                    });
                }
            } else {
                // Si no hay depositoId, remover el valor del filtro si existe
                if (depositoFilterIndex >= 0) {
                    this.filters[depositoFilterIndex].value = null;
                }
            }
        },
        async setFilters(filters) {
            this.pageNumber = 1;
            const numericFields = ['precioTotal', 'descuento', 'recargo'];
            filters.forEach(f => {
                if (numericFields.includes(f.field) && f.value !== null && f.value !== undefined) {
                    if (f.value === '') { f.value = null; return; }
                    const n = Number(f.value);
                    f.value = isNaN(n) ? null : String(n);
                }
            });
            this.filters = filters;
            this.fetch();
        },
        async clearFilters() {
            this.pageNumber = 1;
            this.filters.forEach(f => {
                if (f.field !== 'deposito' && f.field !== 'depositoId') {
                    f.value = null;
                }
            });
            await this.updateDepositoFilter();
            this.fetch();
        },
        performSearch() {
            this.pageNumber = 1
            this.fetch()
        },
        async fetch() {
            // Asegurar que el filtro de dep?sito siempre est? presente antes de hacer la consulta
            await this.updateDepositoFilter();
            this.loading = true
            MovimientoProvider.paginateMovimientos(
                this.pageNumber,
                this.itemsPerPage,
                this.search,
                this.filters,
                this.getOrderBy,
                this.getOrderDesc
            ).then(r => {
                this.items = r.data.movimientoPaginate.items
                this.totalItems = r.data.movimientoPaginate.totalItems

                // Cargar estado de facturas para los movimientos de VENTA
                this.cargarEstadoFacturas()
            }).catch(err => {
                console.error(err)
            }).finally(() => this.loading = false)
        },
        async cargarEstadoFacturas() {
            // Obtener todos los IDs de movimientos
            const movimientosIds = this.items.map(item => item.id);

            if (movimientosIds.length === 0) {
                return;
            }

            try {
                // Una sola llamada al backend con todos los IDs
                const response = await FacturacionProvider.getFacturasExistenPorMovimientos(movimientosIds);

                if (response.data.facturasExistenPorMovimientos) {
                    // Mapear las facturas por movimientoId
                    response.data.facturasExistenPorMovimientos.forEach(factura => {
                        this.$set(this.facturasMap, factura.movimientoId, factura);
                    });
                }
            } catch (error) {
                console.error('Error cargando facturas:', error);
            }
        },
        getFacturaEstado(movimientoId) {
            return this.facturasMap[movimientoId] || null;
        },
        getFacturaColor(movimientoId) {
            const factura = this.facturasMap[movimientoId];
            if (!factura) return 'grey';

            const colores = {
                'EMITIDA': 'success',
                'PENDIENTE': 'warning',
                'ERROR': 'error',
                'PROCESANDO': 'info'
            };
            return colores[factura.estado] || 'grey';
        },
        getFacturaIcon(movimientoId) {
            const factura = this.facturasMap[movimientoId];
            if (!factura) return 'mdi-file-remove';

            const iconos = {
                'EMITIDA': 'mdi-check-circle',
                'PENDIENTE': 'mdi-clock-outline',
                'ERROR': 'mdi-alert-circle',
                'PROCESANDO': 'mdi-loading'
            };
            return iconos[factura.estado] || 'mdi-file';
        },
        getFacturaTexto(movimientoId) {
            const factura = this.facturasMap[movimientoId];
            if (!factura) return 'Sin factura';

            const textos = {
                'EMITIDA': 'Facturada',
                'PENDIENTE': 'Pendiente',
                'ERROR': 'Error',
                'PROCESANDO': 'Procesando'
            };
            return textos[factura.estado] || factura.estado;
        },
        permiteEmitirFacturaPorTipo(item) {
            return item && (item.tipo === 'VENTA' || item.tipo === 'DEVOLUCION');
        },
        mostrarBotonEmitir(item) {
            if (!this.permiteEmitirFacturaPorTipo(item)) return false;
            const factura = this.facturasMap[item.id];

            if (!factura) return true;

            return factura.estado === 'PENDIENTE' || factura.estado === 'ERROR';
        },
        getColor(item) {
            return item.precioTotal > 0 ? 'green' : 'red';
        },
        getTipoColor(item) {
            if (item.tipo === 'DEVOLUCION') return 'orange darken-2';
            if (item.tipo === 'PAGO_ORDEN_VENTA') return 'light-blue darken-1';
            if (item.tipo === 'PAGO_ORDEN_COMPRA') return 'deep-orange darken-1';
            if (item.tipo === 'REEMBOLSO_PAGO_ORDEN_COMPRA') return 'teal darken-1';
            if (item.tipo === 'REEMBOLSO_PAGO_ORDEN_VENTA') return 'red lighten-1';
            return 'blue darken-2';
        },
        getTipoIcon(item) {
            if (item.tipo === 'DEVOLUCION') return 'mdi-keyboard-return';
            if (item.tipo === 'PAGO_ORDEN_VENTA') return 'mdi-cash-check';
            if (item.tipo === 'PAGO_ORDEN_COMPRA') return 'mdi-cash-minus';
            if (item.tipo === 'REEMBOLSO_PAGO_ORDEN_VENTA') return 'mdi-cash-refund';
            return 'mdi-cart';
        },
        getTipoLabel(item) {
            if (item.tipo === 'DEVOLUCION') return 'Devolución';
            if (item.tipo === 'PAGO_ORDEN_VENTA') return 'Orden de venta pagada';
            if (item.tipo === 'PAGO_ORDEN_COMPRA') return 'Orden de compra pagada';
            if (item.tipo === 'REEMBOLSO_PAGO_ORDEN_COMPRA') return 'Reembolso/recupero orden de compra';
            if (item.tipo === 'REEMBOLSO_PAGO_ORDEN_VENTA') return 'Reembolso pago orden de venta';
            if (item.tipo === 'VENTA') return 'Venta';
            return item.tipo || '—';
        },
        create() {
            this.$emit('create');
        },
        devolver() {
            this.$emit('devolver');
        },
        emitirFacturaMovimiento(movimiento) {
            this.$emit('emitir-factura', movimiento);
        },
    }

}
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}
.factura-chip {
    min-width: 100px;
    justify-content: center;
}
</style>
